import { parseUrlPath } from '@st/utils'

const localePattern = /^[a-z]{2}$/
const defaultLocale = 'en'

function isLocale(value: string): boolean {
  return Boolean(localePattern.exec(value))
}

function removeTrailingSlash(value: string): string {
  return value.replace(/\/$/, '') || '/'
}

export function parsePath(path: string): {
  locale: string
  platform: 'desktop' | 'mobile'
  path: string
} {
  let locale = 'en'
  let platform: 'desktop' | 'mobile' = 'desktop'
  let purePath = ''

  const { pathname, search, hash } = parseUrlPath(path)

  const parts = pathname.split('/')
  if (parts[0] === '') parts.splice(0, 1)
  if (isLocale(parts[0])) {
    // eslint-disable-next-line prefer-destructuring
    locale = parts[0]
    parts.splice(0, 1)
  }
  if (parts[0] === 'mobile') {
    // eslint-disable-next-line prefer-destructuring
    platform = parts[0]
    parts.splice(0, 1)
  }

  purePath = removeTrailingSlash(`/${parts.join('/')}${search}${hash}`)

  return {
    locale,
    platform,
    path: purePath,
  }
}

/**
 * Возвращает путь без префиксов платформы и языка (если они были)
 * @example
 * removePrefixes('/ru/mobile/casino/slots') // => '/casino/slots'
 */
export function removePrefixes(path: string): string {
  return parsePath(path).path
}

/**
 * Добавляет/заменяет префиксы платформы и языка на переданные в опциях
 * @example
 * addPrefixes('/casino/slots', { locale: 'ru', platform: 'mobile' })
 * // => '/ru/mobile/casino/slots'
 */
export function addPrefixes(
  path: string,
  options: {
    locale: string
    platform: 'mobile' | 'desktop'
  },
) {
  const localePrefix =
    options.locale === defaultLocale ? '' : `/${options.locale}`
  const platfromPrefix = options.platform === 'mobile' ? `/mobile` : ''

  return removeTrailingSlash(
    `${localePrefix}${platfromPrefix}${removePrefixes(path)}`,
  )
}

/**
 * Проверяет что путь без учета префиксов точно совпадает
 * @example
 * exactlyEquals('/ru/casino', '/casino') // => true
 * exactlyEquals('/ru/casino/slots', '/casino') // => false
 */
export function exactlyEquals(pathToTest: string, pattern: string): boolean {
  return removePrefixes(pathToTest) === removePrefixes(pattern)
}

/**
 * Проверяет что путь без учета префиксов начинается с указанной строки
 * @example
 * startsWith('/ru/casino/slots', '/casino') // => true
 * startsWith('/ru/sport', '/casino') // => false
 */
export function startsWith(pathToTest: string, pattern: string) {
  return removePrefixes(pathToTest).startsWith(removePrefixes(pattern))
}

/**
 * Проверяет что путь без учета префиксов заканчивается указанной строкой
 * @example
 * endsWith('/ru/casino/slots', '/slots') // => true
 * endsWith('/ru/casino/slots/wild', '/slots') // => false
 */
export function endsWith(pathToTest: string, pattern: string) {
  return pathToTest.endsWith(pattern)
}

/**
 * Проверяет что путь без учета префиксов включает указанную строку
 * @example
 * includes('/ru/casino/slots', 'casino') // => true
 * startsincludesWith('/ru/casino/slots/wild', 'sport') // => false
 */
export function includes(pathToTest: string, pattern: string) {
  return !!pathToTest.includes(pattern)
}
